// "Trusted by" logo strip. Real clients live as { name, logo } (image URL) OR
// { name, node } (custom rendered wordmark).
export default function TrustedBy() {
  const logos = [
    { name: "Trishul High School", logo: "/brand/partners/trishul.png", tagline: "Davangere" },
    { name: "KBR Industries", tagline: "Davangere" },
    { name: "Stratos Refining", node: <Wordmark text="STRATOS" subtext="REFINING" weight={800} accent /> },
    { name: "Helix Pharma", node: <Wordmark text="helix" subtext="pharma" italic /> },
    { name: "BluePort Schools", node: <Wordmark text="◆ BluePort" /> },
    { name: "Vector Power", node: <Wordmark text="VECTOR" subtext="//POWER" mono /> },
    { name: "Northwind Chemicals", node: <Wordmark text="NORTHWIND" outline /> },
    { name: "OrbitalWorks", node: <Wordmark text="orbital" subtext="works" condensed /> },
  ];

  return (
    <section className="py-14 border-y border-white/5" data-testid="trusted-by">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center text-xs uppercase tracking-[0.32em] text-[#8C859E] mb-8">
          Trusted by teams across <span className="text-white">9 industries</span>
        </div>

        <div
          className="relative overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
            WebkitMaskImage:
              "linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)",
          }}
        >
          <div className="trustedby-track">
            {[...logos, ...logos].map((l, i) => (
              <div
                key={i}
                className="shrink-0 px-10 md:px-14 opacity-70 hover:opacity-100 transition-all duration-300"
                aria-label={l.name}
                title={l.name}
              >
                {l.logo
                  ? <ImageLogo src={l.logo} name={l.name} tagline={l.tagline}/>
                  : l.node
                  ? l.node
                  : <NameLogo name={l.name} tagline={l.tagline}/>}
              </div>
            ))}
          </div>
        </div>

        <p className="mt-8 text-center text-[11px] text-[#6F687F]">
          Real clients — Trishul High School & KBR Industries, Davangere. More logos coming as engagements grow.
        </p>
      </div>

      <style>{`
        .trustedby-track {
          display: flex;
          align-items: center;
          gap: 0;
          width: max-content;
          animation: trustedby-scroll 42s linear infinite;
        }
        @keyframes trustedby-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .trustedby-track { animation-duration: 90s; }
        }
      `}</style>
    </section>
  );
}

function ImageLogo({ src, name, tagline }) {
  return (
    <div className="flex items-center gap-3 select-none">
      <img
        src={src}
        alt={name}
        className="h-14 w-14 md:h-16 md:w-16 object-contain drop-shadow-[0_0_14px_rgba(59,130,246,0.35)]"
        draggable="false"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <div className="leading-tight">
        <div className="text-white font-semibold text-base md:text-lg tracking-tight">{name}</div>
        {tagline && <div className="text-[10px] uppercase tracking-[0.24em] text-[#8C859E] mt-0.5">{tagline}</div>}
      </div>
    </div>
  );
}

function NameLogo({ name, tagline }) {
  // Text-only client badge (used when we don't have a logo asset yet).
  // Left-side monogram made from the first letters, then the firm name + optional tagline.
  const monogram = name
    .split(/\s+/)
    .slice(0, 3)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div className="flex items-center gap-3 select-none">
      <div className="h-14 w-14 md:h-16 md:w-16 rounded-full border border-[#3B82F6]/40 bg-gradient-to-br from-[#3B82F6]/10 to-[#22D3EE]/10 flex items-center justify-center text-[#3B82F6] font-bold font-[Outfit] text-lg md:text-xl drop-shadow-[0_0_14px_rgba(59,130,246,0.25)]">
        {monogram}
      </div>
      <div className="leading-tight">
        <div className="text-white font-semibold text-base md:text-lg tracking-tight">{name}</div>
        {tagline && <div className="text-[10px] uppercase tracking-[0.24em] text-[#8C859E] mt-0.5">{tagline}</div>}
      </div>
    </div>
  );
}

function Wordmark({ text, subtext, weight = 700, italic, mono, outline, condensed, accent }) {
  const fontFamily = mono
    ? "'JetBrains Mono', monospace"
    : "'Outfit', sans-serif";
  const style = {
    fontFamily,
    fontWeight: weight,
    fontStyle: italic ? "italic" : "normal",
    letterSpacing: condensed ? "-0.04em" : mono ? "0.02em" : "0.01em",
    WebkitTextStroke: outline ? "1.2px #B6B0C7" : undefined,
    color: outline ? "transparent" : accent ? "#3B82F6" : "#D6D2E1",
    fontSize: "1.6rem",
    lineHeight: 1,
  };
  return (
    <div className="flex items-end gap-2 select-none">
      <span style={style}>{text}</span>
      {subtext && (
        <span
          style={{
            fontFamily,
            fontWeight: 400,
            fontStyle: italic ? "italic" : "normal",
            color: "#8C859E",
            fontSize: "0.9rem",
            letterSpacing: mono ? "0.05em" : "0.06em",
            paddingBottom: "0.1rem",
          }}
        >
          {subtext}
        </span>
      )}
    </div>
  );
}
